// A library file's preview (spec docs/specs/web/S1-library-screens.md decision 21), for the
// library page and the Studio's Library tab (G1 decision 29): the files in the shared full-screen
// preview, with live's header (who made the file and when) and live's actions
// (Download, and Delete or Restore for who may change the file, through the library's own
// question), and its details from what the API returns: type, size, shape or length, who made it
// and when, and its folder; a generated file's ⓘ shows first how it was made, its job read when
// ⓘ opens (`GenerationFacts`, G1 decision 30), or, for a creative a brand import brought in from
// live, live's Library Info, its prompt (`ImportedFacts`, #828 bug 24).
import { ActionIcon, Text, Tooltip } from "@mantine/core";
import { Download, RotateCcw, Trash2 } from "lucide-react";
import { useState } from "react";

import { downloadSignedFile } from "../../api/download";
import { useListFolders, useListMembers } from "../../api/generated/endpoints/teams/teams";
import type { Asset, AssetHit } from "../../api/generated/model";
import { memberName } from "../members";
import { isGenerationType } from "../jobs/type-names";
import { hitKey } from "./asset-search";
import { AssetMenu } from "./AssetMenu";
import { GenerationGone, ImportedFacts, JobFacts } from "./GenerationFacts";
import { ImageStructure, showsStructure } from "./ImageStructure";
import { DetailList, DetailSection, MediaPreview, lengthText, type PreviewDetail, type PreviewItem } from "./MediaPreview";
import { usePrefetchTrashDays } from "./trash-days";
import { useImageStructure } from "./use-image-structure";
import { formatDateTime, formatDay } from "../../ui/format";

/** A file's size as people read one: "512 KB", "2.4 MB". */
export function sizeText(bytes: number): string {
  const units = ["B", "KB", "MB", "GB"] as const;
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${unit === 0 || value >= 10 ? Math.round(value) : value.toFixed(1)} ${units[unit]}`;
}

/** A library file as the preview shows it. */
export function previewItem(asset: Asset): PreviewItem {
  return {
    id: asset.id,
    name: asset.name,
    kind: asset.kind,
    url: asset.urls.original,
    // The preview shows an image's lighter picture once it is made; Download keeps the file.
    view: asset.kind === "image" ? (asset.urls.preview ?? null) : null,
    thumb: asset.urls.thumb ?? asset.urls.poster ?? (asset.kind === "image" ? asset.urls.original : null),
    // A file in the trash is not served, but its lighter picture, poster or thumbnail is (PR 894):
    // the preview shows that still (issue 828, bug 30).
    still: asset.urls.original === null ? (asset.urls.preview ?? asset.urls.poster ?? asset.urls.thumb ?? null) : null,
    poster: asset.urls.poster,
    durationMs: asset.duration_ms,
    width: asset.width,
    height: asset.height,
  };
}

/** A search hit as the preview shows it: its key, and a video's at its scene (live's). */
function hitItem(hit: Pick<AssetHit, "asset" | "scene">): PreviewItem {
  const item = previewItem(hit.asset);
  if (hit.scene === null) return item;
  return {
    ...item,
    id: hitKey(hit),
    thumb: hit.scene.thumbnail_url,
    poster: hit.scene.thumbnail_url,
    segment: { start: hit.scene.thumbnail_seconds, end: hit.scene.end_time },
  };
}

export interface LibraryPreviewProps {
  teamId: string;
  brandId: string;
  assets: readonly Asset[];
  /**
   * The search's hits, while the list is a search's (spec S3): the preview steps through them as
   * live's does, each by its `hitKey`, a video's from its scene; `current` is then a hit's key.
   */
  hits?: readonly Pick<AssetHit, "asset" | "scene">[] | undefined;
  current: string | null;
  onShow: (id: string | null) => void;
  more: { has: boolean; loading: boolean; load: () => void };
  trash: boolean;
  mayChange: (asset: Asset) => boolean;
  onDelete: (asset: Asset) => void;
  onRestore: (asset: Asset) => void;
  /** The file being restored, whose button turns. */
  restoring: string | null;
  /** The library's question is open over the preview. */
  paused: boolean;
}

/** What the preview reads from the page's cache, never again by itself: the page's own reads
 *  (the creator chips, the breadcrumbs) keep it fresh, and an empty cache is read once. */
const CACHED = Number.POSITIVE_INFINITY;

export function LibraryPreview(props: LibraryPreviewProps) {
  const { teamId, brandId, assets } = props;
  const prefetchDays = usePrefetchTrashDays(teamId, brandId);
  // Read only while a file is shown, and from the page's own cache where it holds them (the
  // library's creator chips keep them fresh): an open asks nothing it already has (issue 828,
  // item 12; 0 calls, as at #800). The folders are ⓘ's alone (`FolderName`).
  const open = props.current !== null;
  const members = useListMembers(teamId, { query: { enabled: open, staleTime: CACHED } }).data ?? [];
  const [failed, setFailed] = useState<string | null>(null);
  const entries = props.hits ?? assets.map((asset) => ({ asset, scene: null }));
  const byKey = new Map(entries.map((entry) => [hitKey(entry), entry.asset]));
  const assetOf = (item: PreviewItem) => byKey.get(item.id);
  const maker = (asset: Asset) =>
    asset.created_by === null ? "Unknown" : memberName(members.find((member) => member.user_id === asset.created_by));
  return (
    <MediaPreview
      items={entries.map(hitItem)}
      current={props.current}
      onShow={(id) => {
        setFailed(null);
        props.onShow(id);
      }}
      more={props.more}
      paused={props.paused}
      byline={(item) => {
        const asset = assetOf(item);
        return asset === undefined ? null : `${maker(asset)} · ${formatDay(asset.created_at)}`;
      }}
      actions={(item) => {
        const asset = assetOf(item);
        if (asset === undefined) return null;
        return (
          <>
            {failed === asset.id ? <Text size="xs">Download failed. Try again.</Text> : null}
            {item.url === null ? null : (
              <Tooltip label="Download">
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  c="var(--cai-color-viewer-text)"
                  aria-label={`Download ${asset.name}`}
                  onClick={() => {
                    setFailed(null);
                    downloadSignedFile(item.url!, asset.name).catch(() => setFailed(asset.id));
                  }}
                >
                  <Download size={18} aria-hidden />
                </ActionIcon>
              </Tooltip>
            )}
            {!props.mayChange(asset) ? null : props.trash ? (
              <Tooltip label="Restore">
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  c="var(--cai-color-viewer-text)"
                  aria-label={`Restore ${asset.name}`}
                  loading={props.restoring === asset.id}
                  onClick={() => props.onRestore(asset)}
                >
                  <RotateCcw size={18} aria-hidden />
                </ActionIcon>
              </Tooltip>
            ) : (
              <Tooltip label="Delete">
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  c="var(--cai-color-viewer-text)"
                  aria-label={`Delete ${asset.name}`}
                  // Its question names the trash's days: read them as the bin comes near.
                  onPointerEnter={prefetchDays}
                  onFocus={prefetchDays}
                  onClick={() => props.onDelete(asset)}
                >
                  <Trash2 size={18} aria-hidden />
                </ActionIcon>
              </Tooltip>
            )}
            {/* Live's preview "...": the card menu's other items (S1 decision 22). */}
            {props.trash ? null : (
              <AssetMenu
                teamId={teamId}
                brandId={brandId}
                asset={asset}
                mayChange={props.mayChange(asset)}
                onDelete={props.onDelete}
                inPreview
                variant="preview"
              />
            )}
          </>
        );
      }}
      details={(item) => {
        const asset = assetOf(item);
        if (asset === undefined) return null;
        return (
          <FileDetails
            key={asset.id}
            teamId={teamId}
            brandId={brandId}
            asset={asset}
            maker={maker(asset)}
            onLeave={() => props.onShow(null)}
          />
        );
      }}
    />
  );
}

/**
 * ⓘ of a library file: how a generation's file was made, an image's structure (spec
 * library-image-structure), then the file's own facts.
 */
function FileDetails(props: { teamId: string; brandId: string; asset: Asset; maker: string; onLeave: () => void }) {
  const { teamId, brandId, asset } = props;
  const read = useImageStructure(teamId, brandId, asset.id, asset.kind === "image");
  const structure = showsStructure(read) ? (
    <ImageStructure read={read} teamId={teamId} brandId={brandId} assetId={asset.id} />
  ) : null;
  const details: PreviewDetail[] = [
    { label: "Type", value: asset.mime },
    { label: "Size", value: sizeText(asset.bytes) },
  ];
  if (asset.width !== null && asset.height !== null) {
    details.push({ label: "Dimensions", value: `${asset.width} × ${asset.height}` });
  }
  if (asset.duration_ms !== null) details.push({ label: "Length", value: lengthText(asset.duration_ms) });
  details.push(
    { label: "Made by", value: props.maker },
    { label: "Added", value: formatDateTime(asset.created_at) },
    {
      label: "Folder",
      value: asset.folder_id === null ? "Library" : <FolderName teamId={teamId} brandId={brandId} folderId={asset.folder_id} />,
    },
  );
  const file = <DetailList details={details} />;
  // How a generation's file was made. An editor's render is no generation: the file alone. A copy
  // whose generation was deleted says so, with nothing to read. A generation with no job of ours
  // is one a brand import brought in from live: live's Library Info, its prompt (#828).
  const origin = asset.origin;
  if (origin === null && asset.source === "generation") {
    return (
      <>
        <DetailSection title="Info">
          <ImportedFacts teamId={teamId} brandId={brandId} assetId={asset.id} />
        </DetailSection>
        {structure}
        <DetailSection title="File">{file}</DetailSection>
      </>
    );
  }
  if (origin === null || (origin.job_type !== null && !isGenerationType(origin.job_type))) {
    return structure === null ? (
      file
    ) : (
      <>
        {structure}
        <DetailSection title="File">{file}</DetailSection>
      </>
    );
  }
  return (
    <>
      <DetailSection title="How it was made">
        {origin.job_type === null ? (
          <GenerationGone />
        ) : (
          <JobFacts teamId={teamId} brandId={brandId} jobId={origin.job_id} onLeave={props.onLeave} />
        )}
      </DetailSection>
      {structure}
      <DetailSection title="File">{file}</DetailSection>
    </>
  );
}

/** A file's folder's name for ⓘ, read when ⓘ shows it and from the page's cache where it holds
 *  the folders (the library's breadcrumbs). */
function FolderName({ teamId, brandId, folderId }: { teamId: string; brandId: string; folderId: string }) {
  const folders = useListFolders(teamId, brandId, { query: { staleTime: CACHED } }).data;
  return <>{folders?.find((each) => each.id === folderId)?.name ?? "A folder"}</>;
}
